/**
 * Copyright © 2026 Delicious AI, LLC
 */

import styled from '@emotion/styled'
import {
  Box,
  Breadcrumbs,
  Container as MuiContainer,
  CssBaseline,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper as MuiPaper,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DarkMode,
  LightMode,
  Menu as MenuIcon,
  Person,
} from '@mui/icons-material'
import { spacing } from '@mui/system'
import React, { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { auth } from 'api/Firebase'
import Footer from '@/components/Footer'
import GlobalStyle from '@/components/GlobalStyle'
import dashboardItems from '@/components/sidebar/dashboardItems'
import useT from '@/i18n/useT'
import Sidebar from '@/components/sidebar/Sidebar'
import { THEMES } from '@/constants'
import useTheme from '@/hooks/useTheme'
import UserContext from '../context/UserContext'
import { PageHeaderActionsProvider } from './PageHeaderActionsContext'

const drawerWidth = 258
const collapsedDrawerWidth = 84

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`

const Drawer = styled.div`
  ${props => props.theme.breakpoints.up('md')} {
    width: var(--dashboard-drawer-width);
    flex-shrink: 0;
  }
`

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  width: 0;
  ${props => props.theme.breakpoints.up('md')} {
    max-width: calc(100% - var(--dashboard-drawer-width));
  }
`

const Paper = styled(MuiPaper)(spacing)

const Container = styled(MuiContainer)`
  height: 100%;
`

const MainContent = styled(Paper)`
  flex: 1;
  background: ${props => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`
interface DashboardType {
  children?: React.ReactNode
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const router = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [pageHeaderActions, setPageHeaderActions] =
    useState<React.ReactNode>(null)
  const { theme, setTheme } = useTheme()
  const { t } = useT()
  const { user } = React.useContext(UserContext)
  const isDark = theme === THEMES.DARK
  const navItems = React.useMemo(
    () => dashboardItems(t, { isSuperuser: user?.isSuperuser }),
    [t, user?.isSuperuser],
  )

  const getInitials = () => {
    if (displayName) {
      const parts = displayName.trim().split(/\s+/)
      const first = parts[0]?.[0] || ''
      const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : ''
      return `${first}${last}`.toUpperCase()
    }
    if (email) {
      const parts = (email.split('@')[0] || '').split(/[._-]/)
      const first = parts[0]?.[0] || ''
      const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : ''
      return `${first}${last}`.toUpperCase()
    }
    return ''
  }
  const userInitials = getInitials()
  const isMenuOpen = Boolean(menuAnchor)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleToggleTheme = () => {
    setTheme(isDark ? THEMES.DEFAULT : THEMES.DARK)
  }

  const handleLogout = async () => {
    try {
      await auth.signOut()
      window.location.replace('/login')
    } catch (error) {
      console.error(t('dashboard.logout.error', 'Error logging out'), error)
    } finally {
      handleMenuClose()
    }
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false)
  }, [router.pathname])

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setDisplayName(user?.displayName || '')
      setEmail(user?.email || '')
    })

    return () => unsubscribe()
  }, [])

  const getBreadcrumbs = () => {
    const path = router.pathname.split('/').filter(Boolean)
    return path.map((segment, index) => {
      const keyPath = segment
        .split('-')
        .map((part, partIndex) =>
          partIndex === 0
            ? part
            : `${part[0]?.toUpperCase() || ''}${part.slice(1)}`,
        )
        .join('')
      const label = t(`breadcrumbs.${keyPath}`, segment.replace(/-/g, ' '))
      const isLast = index === path.length - 1
      return (
        <Typography
          key={`${segment}-${index}`}
          color={isLast ? 'textPrimary' : 'inherit'}
          variant={isLast ? 'h5' : 'body2'}
          noWrap
          sx={{ textTransform: 'capitalize' }}
        >
          {label}
        </Typography>
      )
    })
  }

  return (
    <Root
      style={
        {
          ['--dashboard-drawer-width' as string]: `${
            sidebarCollapsed ? collapsedDrawerWidth : drawerWidth
          }px`,
        } as React.CSSProperties
      }
    >
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={navItems}
          />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar
            PaperProps={{
              style: {
                width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              },
            }}
            items={navItems}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed(current => !current)}
          />
        </Box>
      </Drawer>
      <AppContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            minHeight: 52,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0 }}>
              <Tooltip title={t('navbar.openDrawer.ariaLabel', 'Open drawer')}>
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    borderRadius: 1,
                  }}
                  aria-label={t('navbar.openDrawer.ariaLabel', 'Open drawer')}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
              <Breadcrumbs>{getBreadcrumbs()}</Breadcrumbs>
            </Box>
            {pageHeaderActions ? (
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {pageHeaderActions}
              </Box>
            ) : null}
          </Box>
          <Tooltip title={email || t('dashboard.account.tooltip', 'Account')}>
            <Box
              role="button"
              onClick={handleMenuOpen}
              sx={theme => ({
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[700]
                    : theme.palette.grey[300],
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                userSelect: 'none',
              })}
              aria-label={t('dashboard.userMenu.ariaLabel', 'User menu')}
            >
              {userInitials || <Person fontSize="small" />}
            </Box>
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={handleToggleTheme}
              sx={{ justifyContent: 'space-between', gap: 4 }}
            >
              {isDark
                ? t('dashboard.theme.lightMode', 'Light mode')
                : t('dashboard.theme.darkMode', 'Dark mode')}
              <ListItemIcon sx={{ minWidth: 'unset' }}>
                {isDark ? (
                  <LightMode fontSize="small" />
                ) : (
                  <DarkMode fontSize="small" />
                )}
              </ListItemIcon>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              {t('common.logout', 'Logout')}
            </MenuItem>
          </Menu>
        </Box>
        <MainContent>
          <PageHeaderActionsProvider
            value={{ setActions: setPageHeaderActions }}
          >
            <Container maxWidth={false} sx={{ py: 1.5 }}>
              {children}
              <Outlet />
            </Container>
          </PageHeaderActionsProvider>
        </MainContent>
        <Footer />
      </AppContent>
    </Root>
  )
}

export default Dashboard
